"use client"

import {
  buildTalentBrowseUrlFromBrief,
  buildTalentSmartSearchUrlFromBrief,
  navigateWithThreadPreservation,
  PaneResponsiveCardGrid,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
} from '@yeshuman/sdk'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Briefcase, Award, ChartNoAxesColumn, Users } from 'lucide-react'
import type { Brief, BriefCandidate } from '../../hooks/use-briefs'
import { BriefCompetencyFramework } from './brief-competency-framework'
import { BriefHiredSuggestions } from './brief-hired-suggestions'
import { BriefSavedCandidateRow } from './brief-saved-candidate-row'
import { BriefSkillsBadges } from './brief-skills-badges'
import { BriefSectionEditAction } from './brief-section-edit-action'
import { BiasCoachBanner, FeedbackBiasSection } from '../bias/bias-coach-banner'
import { useBiasReport } from '../../hooks/use-bias-review'
import { detect, joinedText } from '../../lib/bias-guardrail'

export type BriefCaseFileSectionsProps = {
  brief: Brief
  candidates: BriefCandidate[]
  candidatesLoading: boolean
  onEditBrief?: () => void
  onSwitchToSearchTab?: () => void
}

export function BriefCaseFileSections({
  brief,
  candidates,
  candidatesLoading,
  onEditBrief,
  onSwitchToSearchTab,
}: BriefCaseFileSectionsProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const description = brief.description?.trim()
  const instructions = brief.instructions?.trim()
  const linkedJob = brief.opportunity_title?.trim()
  const liveFindings = detect(joinedText(brief.title, description, instructions), 'job_brief')
  const report = useBiasReport([brief.id, brief.opportunity_id])

  const goToTalentBrowse = () => {
    navigateWithThreadPreservation(navigate, buildTalentBrowseUrlFromBrief(brief), searchParams)
  }

  const goToTalentSmartSearch = () => {
    navigateWithThreadPreservation(navigate, buildTalentSmartSearchUrlFromBrief(brief), searchParams)
  }

  return (
    <div className="space-y-4">
      <Card className="group/section relative z-0">
        {onEditBrief ? (
          <CardHeader showTitle={false}>
            <BriefSectionEditAction label="Edit mandate" onClick={onEditBrief} />
          </CardHeader>
        ) : null}
        <CardContent className={onEditBrief ? undefined : 'pt-6'}>
          <div className="space-y-4 text-sm">
            <p className="whitespace-pre-wrap text-muted-foreground">
              {description || 'No description yet — edit to add.'}
            </p>
            {instructions ? (
              <div className="border-t border-border pt-4">
                <p className="whitespace-pre-wrap text-muted-foreground">{instructions}</p>
              </div>
            ) : null}
            {linkedJob ? (
              <div className="flex items-start gap-2 border-t border-border pt-4 text-muted-foreground">
                <Briefcase className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>{linkedJob}</span>
              </div>
            ) : null}
            <BiasCoachBanner findings={liveFindings} source="edit" />
            <FeedbackBiasSection
              open={report.data?.open ?? liveFindings}
              incidentCount={report.data?.incident_count ?? 0}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="group/section relative z-0">
        <CardHeader>
          <div className="flex w-full items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Award className="h-5 w-5" />
                Skills
              </CardTitle>
              <CardDescription>Required and preferred skills for this brief</CardDescription>
            </div>
            {onEditBrief ? <BriefSectionEditAction label="Edit skills" onClick={onEditBrief} /> : null}
          </div>
        </CardHeader>
        <CardContent>
          <BriefSkillsBadges skills={brief.skills} />
          <BriefHiredSuggestions briefId={brief.id} suggestions={brief.hired_suggestions ?? []} />
        </CardContent>
      </Card>

      <BriefCompetencyFramework brief={brief} />

      {candidatesLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent>
                <div className="mb-2 h-4 w-1/3 rounded bg-muted" />
                <div className="h-3 w-1/2 rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : candidates.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
          <Users className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
          <p className="text-sm font-medium">No candidates yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Browse your talent directory or run analysis to find matches for this brief.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button type="button" variant="outline" onClick={goToTalentBrowse}>
              Browse talent
            </Button>
            {onSwitchToSearchTab ? (
              <Button type="button" variant="outline" onClick={onSwitchToSearchTab}>
                <ChartNoAxesColumn className="mr-2 size-4" />
                Analysis
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={goToTalentSmartSearch}>
                <ChartNoAxesColumn className="mr-2 size-4" />
                Analysis
              </Button>
            )}
          </div>
        </div>
      ) : (
        <PaneResponsiveCardGrid>
          {candidates.map((candidate) => (
            <BriefSavedCandidateRow
              key={candidate.id}
              candidate={candidate}
              briefId={brief.id}
              briefOpportunityId={brief.opportunity_id}
              briefOpportunityTitle={brief.opportunity_title}
              className="h-full"
            />
          ))}
        </PaneResponsiveCardGrid>
      )}
    </div>
  )
}
